import axios from "axios";

/**
 * Resolve the backend base URL.
 *
 * In development we rely on `REACT_APP_BACKEND_URL` (compiled into the JS
 * bundle). In production the bundle is served from the same origin as the
 * backend — so when the app is accessed at `https://krea.empresa.com` the
 * API lives at `https://krea.empresa.com/api`. Using `window.location.origin`
 * at runtime means swapping domains (brand-asset-ai.emergent.host →
 * krea-nuam.wearedatalab.com → krea.wearedatalab.com) no longer requires a
 * rebuild with a different env var.
 *
 * Logic:
 *  1. If the page was served from localhost, use the env var (devs talk to
 *     a remote backend on their laptop).
 *  2. Otherwise, use the current origin — that's where the backend is
 *     mounted behind the platform's ingress.
 */
function resolveBackendUrl() {
    if (typeof window === "undefined") {
        return process.env.REACT_APP_BACKEND_URL || "";
    }
    const host = window.location.hostname;
    const isLocal =
        host === "localhost" || host === "127.0.0.1" || host.endsWith(".local");
    if (isLocal) {
        return process.env.REACT_APP_BACKEND_URL || window.location.origin;
    }
    return window.location.origin;
}

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    timeout: 180000,
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("kc_token");
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    const brandId = localStorage.getItem("kc_current_brand_id");
    if (brandId) {
        config.headers = config.headers || {};
        config.headers["X-Brand-Id"] = brandId;
    }
    return config;
});

api.interceptors.response.use(
    (r) => r,
    async (err) => {
        if (err?.response?.status === 401) {
            const path = window.location.pathname;
            if (!path.startsWith("/login")) {
                localStorage.removeItem("kc_token");
                localStorage.removeItem("kc_user");
                window.location.href = "/login";
            }
            return Promise.reject(err);
        }
        // Transient gateway / origin-unreachable errors. Cloudflare's
        // 520-526 family + classic 502/503/504 all mean "try again in a
        // moment". Without auto-retry, a single bad-luck pod restart or
        // burst-protection trigger nukes every request on the page, the
        // user sees half the UI in error state, and toasts fire all at
        // once. We retry up to 2 times with linear backoff (600ms,
        // 1500ms) ONLY for GET (idempotent). POST/PUT/DELETE are NEVER
        // retried — duplicating a credit charge or a chat message would
        // be far worse than a single failure shown in the UI.
        const status = err?.response?.status;
        const cfg = err?.config;
        const isRetriableStatus =
            status === 502 ||
            status === 503 ||
            status === 504 ||
            (status >= 520 && status <= 526);
        const isGet = (cfg?.method || "get").toLowerCase() === "get";
        // Skip retry for the file-serve endpoint — it's already served
        // directly via <img src>, the browser handles its own retry, and
        // axios isn't actually requesting it. (Defensive: file-serve
        // currently goes through <img>, not axios.)
        const url = cfg?.url || "";
        if (
            isRetriableStatus &&
            isGet &&
            cfg &&
            !cfg.__kcRetried &&
            !url.includes("/files/serve/")
        ) {
            cfg.__kcRetried = 0;
        }
        if (
            isRetriableStatus &&
            isGet &&
            cfg &&
            typeof cfg.__kcRetried === "number" &&
            cfg.__kcRetried < 2
        ) {
            const attempt = cfg.__kcRetried + 1;
            cfg.__kcRetried = attempt;
            const delay = attempt === 1 ? 600 : 1500;
            await new Promise((res) => setTimeout(res, delay));
            return api.request(cfg);
        }
        return Promise.reject(err);
    },
);

export const fileUrl = (storagePath) => {
    const token = localStorage.getItem("kc_token");
    return `${API}/files/serve/${storagePath}?auth=${encodeURIComponent(token || "")}`;
};

/**
 * Lightweight thumbnail served by the backend (Pillow → WebP).
 * Allowed widths: 160, 320, 480, 640, 960. Use for grid cards to keep
 * /library and /references fast.
 */
export const thumbUrl = (storagePath, w = 320) => {
    const token = localStorage.getItem("kc_token");
    return `${API}/files/serve/${storagePath}?w=${w}&auth=${encodeURIComponent(token || "")}`;
};

/**
 * Turn an axios error into a string safe to render in toasts / banners.
 *
 * FastAPI returns 422 validation errors as an ARRAY of objects with the
 * shape `{type, loc, msg, input, ctx, url}` under `detail`. Passing that
 * array straight to <toast.error/> made React crash with error #31
 * ("objects are not valid as a React child"). This helper flattens the
 * array into a readable Spanish-friendly string and handles the common
 * shapes:
 *   - axios network error (no response)
 *   - 4xx/5xx with `detail` as a plain string
 *   - 422 with `detail` as an array of pydantic errors
 *   - fallback to a generic message
 */
export const extractErrorMessage = (err, fallback = "Algo salió mal") => {
    if (!err) return fallback;
    if (typeof err === "string") return err;
    // Axios network error (no server response)
    if (err.request && !err.response) {
        return "No se pudo conectar con el servidor. Revisa tu conexión.";
    }
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (Array.isArray(detail)) {
        const parts = detail
            .map((e) => {
                const field = Array.isArray(e?.loc)
                    ? e.loc.filter((p) => p !== "body").join(".")
                    : "";
                const msg = e?.msg || "valor inválido";
                return field ? `${field}: ${msg}` : msg;
            })
            .filter(Boolean);
        if (parts.length) return parts.join(" · ");
    }
    if (err?.message && typeof err.message === "string") return err.message;
    return fallback;
};
